import { useAthleteTransferOrgGroupsQuery } from "apps/web/src/types";
import { useUser } from "apps/web/src/utils/tokens";
import { Route, Routes } from "react-router-dom-v5-compat";
import { useNutritionFlags } from "../../../../hooks/useNutritionFlags";
import { AdminRoute, ProtectedRoute, RoleRoute } from "../../ProtectedRoutes";
import { SecondaryNavLayout, SecondaryNavLayoutProps } from "../../SecondaryNav/SecondaryNavLayout";
import { GoalsTemplatePage } from "./GoalsTemplatePage";
import { TagsPage } from "./TagsPage";
import {
  NAV_TEAM_MANAGEMENT_GOALS,
  NAV_TEAM_MANAGEMENT_TAGS,
  NAV_TEAM_MANAGEMENT_TEAMS,
  NAV_TEAM_MANAGEMENT_TRANSFERS,
  teamManagementLink,
} from "./TeamManagementPaths";
import { TeamsPage } from "./TeamsPage";
import { TransfersPage } from "./TransfersPage";

interface SecondaryNavLayoutPropsArgs {
  isTransfersAllowed: boolean;
  isTeamScopedPermissionsEnabled: boolean;
}

const transfersLink = {
  name: "Transfers",
  to: NAV_TEAM_MANAGEMENT_TRANSFERS,
  requiresAdmin: true,
};

const goalTemplateLink = {
  name: "Goals",
  to: NAV_TEAM_MANAGEMENT_GOALS,
};

const getSecondaryNavLayoutProps = ({ isTransfersAllowed, isTeamScopedPermissionsEnabled }: SecondaryNavLayoutPropsArgs): SecondaryNavLayoutProps => ({
  current: teamManagementLink,
  enableAthleteSearch: true,
  tabs: [
    {
      name: "Teams",
      to: NAV_TEAM_MANAGEMENT_TEAMS,
    },
    ...(!isTeamScopedPermissionsEnabled ? [{ name: "Tags", to: NAV_TEAM_MANAGEMENT_TAGS }] : []),
    ...(isTransfersAllowed ? [transfersLink] : []),
    {
      name: "Goals",
      to: NAV_TEAM_MANAGEMENT_GOALS,
    },
  ],
});

export const TeamManagementRouter = () => {
  const user = useUser();
  const { isTeamScopedPermissionsEnabled } = useNutritionFlags();
  const { data: athleteTransferOrgGroupsData } = useAthleteTransferOrgGroupsQuery({
    skip: !(user?.orgMembership?.roles.includes("dietitian") || user?.orgMembership?.roles.includes("nonRdAdmin")),
  });
  const isTransfersAllowed = (athleteTransferOrgGroupsData?.athleteTransferOrgGroups.length ?? 0) > 0;

  return (
    <Routes>
      <Route element={<SecondaryNavLayout {...getSecondaryNavLayoutProps({ isTransfersAllowed, isTeamScopedPermissionsEnabled })} />}>
        <Route element={<RoleRoute roles={["dietitian", "nonRdAdmin"]} />}>
          <Route index element={<TeamsPage />} />
          <Route path={NAV_TEAM_MANAGEMENT_TEAMS} element={<TeamsPage />} />
          {!isTeamScopedPermissionsEnabled && <Route path={NAV_TEAM_MANAGEMENT_TAGS} element={<TagsPage />} />}
          <Route path={NAV_TEAM_MANAGEMENT_GOALS} element={<GoalsTemplatePage />} />
          <Route
            path={NAV_TEAM_MANAGEMENT_TRANSFERS}
            element={
              <ProtectedRoute isAllowed={() => isTransfersAllowed}>
                <AdminRoute>
                  <TransfersPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
};
